import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import PageHero from '../components/layout/PageHero';

type Publication = {
  _id: string;
  year?: number;
  type: 'journal' | 'patent' | 'other';
  title: string;
  authors: string;
  venue?: string;
  url?: string;
  isHighlighted?: boolean;
};

const renderHtml = (html?: string) => ({ __html: html || '' });
const stripHtml = (value: string = '') =>
  value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const DEFAULT_INTRO =
  'This page presents recent journal papers, patents, and other representative outputs maintained in the admin system.';

const Publications: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [intro, setIntro] = useState(DEFAULT_INTRO);
  const [activeYear, setActiveYear] = useState<number | 'all'>('all');
  const [activeType, setActiveType] = useState<
    'journal' | 'patent' | 'other'
  >('journal');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get<Publication[]>('/publications');
        setItems(res.data || []);
      } catch (err) {
        console.error(err);
        setError('Failed to load publications. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    api.get<{ publications: string }>('/page-intros').then((res) => {
      const v = res.data?.publications?.trim();
      if (v) setIntro(v);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const type = searchParams.get('type');
    if (
      type === 'journal' ||
      type === 'patent' ||
      type === 'other'
    ) {
      setActiveType(type);
    }
  }, [searchParams]);

  const normalizeType = (type?: string): Publication['type'] =>
    type === 'conference' ? 'other' : (type as Publication['type']);

  const normalizedItems = useMemo(
    () =>
      (items || []).map((p) => ({
        ...p,
        type: normalizeType(p.type),
      })),
    [items]
  );

  const typeFiltered = useMemo(
    () => normalizedItems.filter((p) => p.type === activeType),
    [normalizedItems, activeType]
  );

  const yearOptions = useMemo(
    () =>
      Array.from(
        new Set(
          typeFiltered
            .map((p) => p.year)
            .filter((y): y is number => typeof y === 'number')
        )
      ).sort((a, b) => b - a),
    [typeFiltered]
  );

  const filtered = useMemo(
    () =>
      typeFiltered.filter((p) =>
        activeYear === 'all' ? true : p.year === activeYear
      ),
    [typeFiltered, activeYear]
  );

  const years = useMemo(
    () =>
      Array.from(
        new Set(
          filtered
            .map((p) => p.year)
            .filter((y): y is number => typeof y === 'number')
        )
      ).sort((a, b) => b - a),
    [filtered]
  );

  const featuredList = useMemo(() => {
    const highlighted = typeFiltered
      .filter((p) => p.isHighlighted)
      .sort((a, b) => (b.year || 0) - (a.year || 0));

    const byYear = new Map<number, Publication>();
    highlighted.forEach((pub) => {
      if (typeof pub.year !== 'number') return;
      if (!byYear.has(pub.year)) byYear.set(pub.year, pub);
    });

    const picked = Array.from(byYear.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([, pub]) => pub);

    if (picked.length > 0) return picked.slice(0, 4);

    // Fallback when no highlighted items: pick latest item per year
    const fallbackByYear = new Map<number, Publication>();
    typeFiltered
      .slice()
      .sort((a, b) => (b.year || 0) - (a.year || 0))
      .forEach((pub) => {
        if (typeof pub.year !== 'number') return;
        if (!fallbackByYear.has(pub.year)) fallbackByYear.set(pub.year, pub);
      });

    return Array.from(fallbackByYear.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([, pub]) => pub)
      .slice(0, 4);
  }, [typeFiltered]);

  useEffect(() => {
    if (activeYear === 'all') return;
    if (!yearOptions.includes(activeYear)) {
      setActiveYear('all');
    }
  }, [activeYear, yearOptions]);

  return (
    <section className="bg-surface min-h-full">
      <PageHero title="Publications" intro={intro} sectionLabel="Research Outputs" />
      <div className="mx-auto max-w-screen-2xl px-8 pb-20 pt-12">

        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12">
          <div className="lg:col-span-9">
            <div className="mb-8 flex flex-wrap gap-1 border-b-2 border-um-navy/70 pb-0">
              {[
                { key: 'journal', label: 'Journal Articles' },
                { key: 'patent', label: 'Patents' },
                { key: 'other', label: 'Other Outputs' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() =>
                    setActiveType(
                      tab.key as 'journal' | 'patent' | 'other'
                    )
                  }
                  className={`um-nav-tab ${
                    activeType === tab.key
                      ? 'um-nav-tab-active'
                      : ''
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="mb-8 flex items-center gap-3">
              <label htmlFor="publication-year-filter" className="text-sm text-on-surface-variant">
                Year
              </label>
              <select
                id="publication-year-filter"
                className="um-input w-auto min-w-[180px] bg-white px-3 py-2 text-sm"
                value={activeYear === 'all' ? 'all' : String(activeYear)}
                onChange={(e) =>
                  setActiveYear(e.target.value === 'all' ? 'all' : Number(e.target.value))
                }
              >
                <option value="all">All years</option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {loading ? (
              <p className="text-on-surface-variant">Loading...</p>
            ) : error ? (
              <div className="rounded-md bg-error-container px-4 py-3 text-sm text-on-error-container">
                {error}
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-on-surface-variant">No publication data available yet.</p>
            ) : (
              <div className="space-y-10">
                {years.map((year) => (
                  <section key={year} className="space-y-6">
                    <div className="sticky top-[88px] z-10 -mx-2 bg-surface/90 px-2 py-3 backdrop-blur-sm">
                      <h2 className="text-3xl font-semibold leading-tight text-primary md:text-4xl">{year}</h2>
                    </div>
                    <div className="space-y-6">
                      {filtered
                        .filter((p) => p.year === year)
                        .map((pub) => (
                          <article
                            key={pub._id}
                            className="um-card bg-surface-container-lowest p-6 transition-colors hover:bg-surface-container-low"
                          >
                            <h3 className="text-3xl font-semibold leading-tight text-on-surface md:text-4xl">
                              <Link to={`/publications/${pub._id}`} className="hover:text-primary">
                                <span dangerouslySetInnerHTML={renderHtml(pub.title)} />
                              </Link>
                            </h3>
                            <p className="mt-3 text-on-surface-variant" dangerouslySetInnerHTML={renderHtml(pub.authors)} />
                            {pub.venue && (
                              <p className="mt-2 text-sm italic text-on-surface-variant" dangerouslySetInnerHTML={renderHtml(pub.venue)} />
                            )}
                            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-primary">
                              {pub.url && (
                                <a href={pub.url} target="_blank" rel="noreferrer" className="um-btn-link text-sm">
                                  ↪ External link
                                </a>
                              )}
                              <Link to={`/publications/${pub._id}`} className="um-btn-link text-sm">
                                View details
                              </Link>
                            </div>
                          </article>
                        ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>

          <aside className="space-y-4 lg:col-span-3">
            <section className="um-card bg-surface-container-low p-6">
              <h3 className="mb-4 flex items-center gap-2 text-sm uppercase tracking-widest text-primary font-semibold">
                <span>★</span> Featured Outputs
              </h3>
              <div className="space-y-6">
                {featuredList.map((f, i) => (
                  <div key={f._id} className={i > 0 ? 'border-t border-outline-variant/20 pt-6' : ''}>
                    <p className="mb-1 text-xs uppercase tracking-wider text-on-surface-variant">
                      {f.year || 'Year N/A'}
                    </p>
                    <h4 className="text-3xl font-semibold leading-tight text-on-surface">
                      <Link to={`/publications/${f._id}`} className="hover:text-primary">
                        <span dangerouslySetInnerHTML={renderHtml(f.title)} />
                      </Link>
                    </h4>
                    <p className="mt-2 line-clamp-2 text-sm text-on-surface-variant">
                      {stripHtml(f.authors)}
                    </p>
                    <Link to={`/publications/${f._id}`} className="um-btn-link mt-2 text-sm">
                      View article →
                    </Link>
                  </div>
                ))}
                {featuredList.length === 0 && (
                  <p className="text-sm text-on-surface-variant">No featured outputs yet.</p>
                )}
              </div>
            </section>

          </aside>
        </div>
      </div>
    </section>
  );
};

export default Publications;

